import React, { useEffect, useMemo, useState } from "react";
import { NumberDraftInput } from "./NumberDraftInput";
import { Drawer } from "vaul";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PositionData } from "@crypto-trading/common/browser";
import { openPosition, getOpenPositions } from "@/api/positions";
import { useUserExchanges, exchangeKeys } from "@/hooks/useExchanges";
import {
  useSymbolInfo,
  useTokenAvailability,
  useTokenData,
  use24hTicker,
} from "@/hooks/useMarketData";
import { ensureUsdtSymbol, extractBaseCurrency } from "@/utils/symbols";
import { getTradeErrorMessage } from "@/utils/trade-error";
import {
  belowMinimumTradeMessage,
  minimumPositionNotional,
  minimumTradeMargin,
  specificOpenError,
} from "@/utils/trade-minimum";
import { TokenLiveChart } from "./TokenLiveChart";
import { TabSelector } from "./TabSelector";
import { ExchangeSelect } from "./ExchangeSelect";
import { PriceMoveRoiHint } from "./PriceMoveRoiHint";
import { TpSlTargetPrice } from "./TpSlTargetPrice";
import {
  TakeProfitModeSwitch,
  TrailingTakeProfitExplainer,
  TrailingTakeProfitInputs,
  takeProfitPayload,
  trailingInputsError,
} from "./TrailingTakeProfit";
import {
  TRAILING_DEFAULT_ACTIVATION_PERCENT,
  TRAILING_DEFAULT_DISTANCE_PERCENT,
  stopLossPastLiquidationError,
} from "@crypto-trading/common/browser";
import { useNavigate } from "react-router-dom";
import useTokenLivePrice, {
  LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import { FundingRate } from "./FundingRate";

const EXCHANGE_NAME_MAP: Record<string, string> = {
  binance: "Binance",
  bybit: "Bybit",
  gateio: "Gate.io",
  aster: "Aster",
  kucoin: "KuCoin",
  hyperliquid: "Hyperliquid",
};

interface PositionTradeDrawerProps {
  userId: string;
  symbol: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoToPosition: (position: PositionData | null) => void;
  /** Shown in the header instead of the raw order symbol. */
  displayTicker?: string;
  /** When set, only these user-exchange codes can be selected. */
  allowedExchangeCodes?: string[];
  /** Per-exchange order symbols for tokenized stocks. */
  symbolByExchangeCode?: Record<string, string>;
}

export const PositionTradeDrawer: React.FC<PositionTradeDrawerProps> = ({
  userId,
  symbol,
  open,
  onOpenChange,
  onGoToPosition,
  displayTicker,
  allowedExchangeCodes,
  symbolByExchangeCode,
}) => {
  const navigate = useNavigate();
  const normalizedSymbol = symbol ? ensureUsdtSymbol(symbol) : "";
  const baseCurrency = normalizedSymbol
    ? extractBaseCurrency(normalizedSymbol)
    : "";

  const [positionType, setPositionType] = useState<"LONG" | "SHORT">("LONG");
  const [amount, setAmount] = useState<number>(0);
  const [leverage, setLeverage] = useState<number>(5);
  const [takeProfitPercent, setTakeProfitPercent] = useState<string>("");
  const [stopLossPercent, setStopLossPercent] = useState<string>("");
  const [trailingTakeProfit, setTrailingTakeProfit] = useState(false);
  const [trailingActivation, setTrailingActivation] = useState(
    String(TRAILING_DEFAULT_ACTIVATION_PERCENT),
  );
  const [trailingDistance, setTrailingDistance] = useState(
    String(TRAILING_DEFAULT_DISTANCE_PERCENT),
  );
  const [selectedExchangeCode, setSelectedExchangeCode] = useState<
    string | undefined
  >(undefined);
  const [tradeSuccess, setTradeSuccess] = useState(false);
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [isFetchingPosition, setIsFetchingPosition] = useState(false);
  const catalogLimited = Boolean(allowedExchangeCodes);

  const orderSymbol = useMemo(() => {
    if (selectedExchangeCode && symbolByExchangeCode?.[selectedExchangeCode]) {
      return ensureUsdtSymbol(symbolByExchangeCode[selectedExchangeCode]);
    }
    return normalizedSymbol;
  }, [normalizedSymbol, selectedExchangeCode, symbolByExchangeCode]);

  const queryClient = useQueryClient();

  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);

  const selectedExchange = useMemo(
    () => exchanges.find((exchange) => exchange.code === selectedExchangeCode),
    [exchanges, selectedExchangeCode],
  );

  const maxBalance = selectedExchange?.balance?.free ?? 0;

  const livePriceExchangeCode = useMemo(() => {
    if (selectedExchangeCode === "aster") return "asterdex";
    return selectedExchangeCode as LivePriceExchangeCode;
  }, [selectedExchangeCode]);
  // Same live price as the desktop drawer, so TP/SL and liquidation match.
  const { price: livePrice, funding } = useTokenLivePrice(
    orderSymbol,
    livePriceExchangeCode,
    open && !!selectedExchangeCode,
  );

  const { data: tokenData, isLoading: isLoadingTokenData } = useTokenData(
    userId,
    orderSymbol,
    selectedExchangeCode,
    open && !!selectedExchangeCode,
  );
  const referencePrice = livePrice ?? tokenData?.lastPrice;

  const { data: ticker24h, isLoading: isLoading24hTicker } = use24hTicker(
    userId,
    orderSymbol,
    selectedExchangeCode,
    open && !!selectedExchangeCode,
  );

  const { data: symbolInfo, isLoading: isLoadingSymbolInfo } = useSymbolInfo(
    userId,
    orderSymbol,
    selectedExchangeCode,
    open,
  );

  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, orderSymbol, open && !catalogLimited);

  // Backend returns all globally available exchanges in userExchanges field
  // We need to filter against user's actually connected exchanges
  const globallyAvailableExchangeCodes = useMemo(() => {
    return tokenAvailability?.userExchanges ?? [];
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes],
  );

  // Filter user's connected exchanges to only those that support the token
  const supportedUserExchangeCodes = useMemo(() => {
    const connected = exchanges.map((e) => e.code);
    if (catalogLimited) {
      const allowed = new Set(allowedExchangeCodes);
      return connected.filter((code) => allowed.has(code));
    }
    return connected.filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, catalogLimited, allowedExchangeCodes, globallyAvailableSet]);

  const supportedExchangeSet = useMemo(
    () => new Set(supportedUserExchangeCodes),
    [supportedUserExchangeCodes],
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;

  // Show setup prompt if user has no connected exchanges that support the token,
  // but there are other exchanges globally that do support it
  const listedExchangeCodes = catalogLimited
    ? (allowedExchangeCodes ?? [])
    : globallyAvailableExchangeCodes;
  const availabilityPending = catalogLimited ? false : isLoadingAvailability;
  const showSetupPrompt =
    !availabilityPending &&
    !isLoadingExchanges &&
    !hasTradableExchange &&
    listedExchangeCodes.length > 0;
  const tokenUnavailableEverywhere =
    !availabilityPending &&
    !isLoadingExchanges &&
    !hasTradableExchange &&
    !showSetupPrompt;

  const maxLeverage = symbolInfo?.maxLeverage ?? 50;

  const positionNotional = useMemo(
    () => minimumPositionNotional(symbolInfo, referencePrice),
    [symbolInfo, referencePrice],
  );
  const minAmount = useMemo(
    () => minimumTradeMargin(positionNotional, leverage),
    [positionNotional, leverage],
  );

  useEffect(() => {
    if (!open) {
      setTradeError(null);
      setTradeSuccess(false);
      setAmount(0);
      setTakeProfitPercent("");
      setStopLossPercent("");
      setTrailingTakeProfit(false);
      setTrailingActivation(String(TRAILING_DEFAULT_ACTIVATION_PERCENT));
      setTrailingDistance(String(TRAILING_DEFAULT_DISTANCE_PERCENT));
      setIsFetchingPosition(false);
      return;
    }

    if (!hasTradableExchange) {
      setSelectedExchangeCode(undefined);
      return;
    }

    // Exchanges come back in the user's Connected Exchanges priority order.
    const preferredOrder = exchanges.map((exchange) => exchange.code);

    const nextSupported =
      preferredOrder.find((code) => supportedExchangeSet.has(code)) ??
      supportedUserExchangeCodes[0];

    if (
      !selectedExchangeCode ||
      !supportedExchangeSet.has(selectedExchangeCode)
    ) {
      if (nextSupported) {
        setSelectedExchangeCode(nextSupported);
      }
    }
  }, [
    open,
    hasTradableExchange,
    exchanges,
    supportedUserExchangeCodes,
    supportedExchangeSet,
    selectedExchangeCode,
  ]);

  useEffect(() => {
    if (symbolInfo?.maxLeverage && leverage > symbolInfo.maxLeverage) {
      setLeverage(symbolInfo.maxLeverage);
    }
  }, [symbolInfo?.maxLeverage, leverage]);

  const formattedBalance = useMemo(() => {
    if (!selectedExchange) {
      return "--";
    }
    return `${selectedExchange.balance?.free?.toFixed(2) ?? "0.00"} USDT`;
  }, [selectedExchange]);

  const percentFilled =
    maxBalance > 0 ? Math.min(100, (amount / maxBalance) * 100) : 0;

  const isActionDisabled =
    !userId ||
    !orderSymbol ||
    !selectedExchangeCode ||
    amount <= 0 ||
    leverage <= 0 ||
    maxBalance <= 0 ||
    !hasTradableExchange ||
    stopLossPastLiquidationError(Number(stopLossPercent), leverage) != null ||
    (trailingTakeProfit &&
      trailingInputsError(trailingActivation, trailingDistance) != null);

  const tradeMutation = useMutation({
    mutationFn: async () => {
      if (
        !userId ||
        !orderSymbol ||
        !selectedExchangeCode ||
        !hasTradableExchange
      ) {
        throw new Error("Missing trade parameters");
      }

      // Validate minimum amount if symbolInfo is available
      if (positionNotional > 0 && minAmount > 0 && amount < minAmount) {
        throw new Error(belowMinimumTradeMessage(positionNotional, leverage));
      }

      const payload = {
        userId,
        symbol: orderSymbol,
        exchangeCode: selectedExchangeCode,
        positionType,
        amount,
        leverage,
        ...takeProfitPayload(
          trailingTakeProfit,
          takeProfitPercent,
          trailingActivation,
          trailingDistance,
        ),
        stopLossPercent: stopLossPercent ? Number(stopLossPercent) : undefined,
      };

      return openPosition(payload);
    },
    onSuccess: async () => {
      setTradeError(null);
      setTradeSuccess(true);
      const invalidatePromises: Promise<unknown>[] = [
        queryClient.invalidateQueries({
          queryKey: ["positions", "open"],
          exact: false,
        }),
      ];

      if (userId) {
        invalidatePromises.push(
          queryClient.invalidateQueries({
            queryKey: exchangeKeys.userExchanges(userId, true),
          }),
          queryClient.invalidateQueries({
            queryKey: exchangeKeys.userExchanges(userId, false),
          }),
        );
      }

      await Promise.all(invalidatePromises);
    },
    onError: (error) => {
      setTradeError(
        specificOpenError(getTradeErrorMessage(error, "Failed to open trade"), {
          amount,
          leverage,
          positionNotional,
        }),
      );
    },
  });

  const handleAmountChange = (value: number) => {
    if (!Number.isFinite(value)) {
      setAmount(0);
      return;
    }

    // Whole USDT only, never above the balance
    setAmount(Math.max(0, Math.floor(Math.min(value, maxBalance))));
  };

  const handleQuickAmount = (percent: number) => {
    if (!maxBalance || !hasTradableExchange) return;
    handleAmountChange((percent / 100) * maxBalance);
  };

  const handleGoToPosition = async () => {
    if (!userId || !orderSymbol || !hasTradableExchange) return;
    setIsFetchingPosition(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: ["positions", "open"],
        exact: false,
      });
      const positions = await getOpenPositions(userId);
      const latest =
        positions.find(
          (pos) =>
            pos.symbol?.toUpperCase() === orderSymbol.toUpperCase() &&
            pos.positionType === positionType,
        ) || null;
      if (!latest) {
        setTradeError(
          "Unable to locate the new position yet. Please wait a moment and retry.",
        );
        return;
      }
      onOpenChange(false);
      onGoToPosition(latest);
    } catch (error) {
      setTradeError(
        error instanceof Error
          ? error.message
          : "Unable to open the position view right now",
      );
    } finally {
      setIsFetchingPosition(false);
    }
  };

  if (!symbol || !open) {
    return null;
  }

  // Get unique base exchange names that support the token but user hasn't connected
  const userConnectedBaseCodes = new Set(
    exchanges.map((e) => e.code.replace("-demo", "")),
  );
  const setupSourceCodes = catalogLimited
    ? listedExchangeCodes
    : globallyAvailableExchangeCodes;
  const setupExchangeNames = [
    ...new Set(
      setupSourceCodes
        .map((code) => code.replace("-demo", ""))
        .filter((code) => !userConnectedBaseCodes.has(code)),
    ),
  ].map((code) => EXCHANGE_NAME_MAP[code] || code.toUpperCase());

  const handleNavigateToSettings = () => {
    onOpenChange(false);
    navigate("/settings");
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      repositionInputs={false}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content className="bg-primary flex rounded-t-3xl flex-col fixed bottom-0 left-0 right-0 max-h-[95vh] z-[121]">
          <div className="px-4 border-b border-white/10 pb-3 pt-3 sticky top-0 bg-primary z-10 rounded-t-3xl">
            <Drawer.Handle className="mx-auto mb-3 mt-0 h-1 w-10 rounded-full" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-secondary mb-1">Trade</p>
                <h2 className="text-base font-semibold text-white">
                  {displayTicker || baseCurrency || orderSymbol}
                </h2>
              </div>
              <span className="text-xs text-text-secondary">
                {selectedExchange ? selectedExchange.name : "No exchange"}
              </span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-4 p-4">
            <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-3 space-y-3">
              <TokenLiveChart
                userId={userId}
                symbol={orderSymbol}
                exchangeCode={selectedExchangeCode}
                enabled={open}
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-4">
              <p className="text-xs font-medium text-[#a3a3a3]">
                Market data
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-text-secondary mb-1">Last Price</p>
                  <p className="text-sm font-semibold text-text-primary">
                    {referencePrice ? `$${referencePrice.toFixed(4)}` : "---"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h High</p>
                  <p className="text-sm font-semibold text-green-400">
                    {ticker24h?.highPrice
                      ? `$${ticker24h.highPrice.toFixed(4)}`
                      : "---"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h Low</p>
                  <p className="text-sm font-semibold text-red-400">
                    {ticker24h?.lowPrice
                      ? `$${ticker24h.lowPrice.toFixed(4)}`
                      : "---"}
                  </p>
                </div>
                <FundingRate funding={funding} />
              </div>
              {(isLoadingTokenData || isLoading24hTicker) && (
                <div className="flex items-center justify-center text-xs text-text-secondary">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Updating data...
                </div>
              )}
            </div>

            {isLoadingAvailability && (
              <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 text-xs text-text-secondary">
                Checking exchange availability...
              </div>
            )}

            {showSetupPrompt && (
              <div className="rounded-xl bg-[rgba(13,18,26,0.7)] p-4 border border-yellow-500/40 space-y-3">
                <p className="text-sm font-semibold text-yellow-300">
                  Token not available on your connected exchanges
                </p>
                <p className="text-xs text-text-secondary">
                  You can still trade it by connecting one of these exchanges:
                </p>
                <div className="flex flex-wrap gap-2">
                  {setupExchangeNames.map((name) => (
                    <span
                      key={name}
                      className="px-3 py-1 rounded-full bg-yellow-500/10 text-yellow-200 text-xs border border-yellow-500/30"
                    >
                      {name}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleNavigateToSettings}
                  className="w-full rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover"
                >
                  Go to Settings
                </button>
              </div>
            )}

            {tokenUnavailableEverywhere && (
              <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 text-sm text-text-secondary">
                This token is not yet available on any of our supported
                exchanges. Check back soon!
              </div>
            )}

            {hasTradableExchange && (
              <>
                <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-[#a3a3a3]">
                      Exchange To Trade
                    </p>
                    {selectedExchangeCode && (
                      <span className="text-xs text-text-secondary">
                        Balance: {formattedBalance}
                      </span>
                    )}
                  </div>
                  <ExchangeSelect
                    exchanges={exchanges}
                    selectedExchangeCode={selectedExchangeCode}
                    supportedExchangeCodes={supportedExchangeSet}
                    isLoading={isLoadingExchanges}
                    onSelect={(exchangeCode) => {
                      setSelectedExchangeCode(exchangeCode);
                      setAmount(0);
                    }}
                    disabled={exchanges.length === 0}
                  />
                  {exchanges.length === 0 && !isLoadingExchanges && (
                    <p className="text-xs text-red-400">
                      You need to connect an exchange before trading.
                    </p>
                  )}
                  {!selectedExchangeCode && exchanges.length > 0 && (
                    <p className="text-xs text-text-secondary">
                      Select an exchange to continue
                    </p>
                  )}
                </div>

                {selectedExchangeCode && (
                  <>
                    <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-4">
                      <p className="text-xs font-medium text-[#a3a3a3]">
                        Direction
                      </p>
                      <TabSelector
                        options={[
                          { value: "LONG", label: "Long" },
                          { value: "SHORT", label: "Short" },
                        ]}
                        value={positionType}
                        onChange={(value) =>
                          setPositionType(value as "LONG" | "SHORT")
                        }
                        variant="compact"
                      />
                    </div>

                    <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-[#a3a3a3]">
                          Amount
                        </p>
                        <span className="text-xs text-text-secondary">
                          {percentFilled.toFixed(0)}% of balance
                        </span>
                      </div>
                      {minAmount > 0 && (
                        <p
                          id="trade-min-amount"
                          className="text-xs text-text-secondary"
                        >
                          Min: {minAmount} USDT
                        </p>
                      )}
                      <NumberDraftInput
                        id="trade-amount-input"
                        min={0}
                        max={maxBalance}
                        step="1"
                        className="w-full rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-2.5 text-sm text-white focus:border-[#2dcc89] focus:outline-none"
                        value={amount}
                        format={(value) => (value > 0 ? String(value) : "")}
                        emptyValue={0}
                        wholeNumbers
                        onValueChange={handleAmountChange}
                        placeholder="0 USDT"
                        disabled={maxBalance <= 0}
                      />
                      <input
                        type="range"
                        min={0}
                        max={maxBalance || 1}
                        step={Math.max(Math.floor((maxBalance || 1) / 100), 1)}
                        value={Math.min(amount, maxBalance || 1)}
                        onChange={(event) =>
                          handleAmountChange(Number(event.target.value))
                        }
                        disabled={maxBalance <= 0}
                        className="w-full accent-emerald-400"
                      />
                      <div className="flex justify-between gap-4 text-xs text-text-secondary">
                        {[25, 50, 75, 100].map((percent) => (
                          <button
                            key={percent}
                            type="button"
                            onClick={() => handleQuickAmount(percent)}
                            disabled={maxBalance <= 0}
                            className="flex-1 rounded-full border border-white/15 bg-[#171717] px-2 py-1 text-white hover:bg-[#262626] disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {percent}%
                          </button>
                        ))}
                      </div>
                      {maxBalance <= 0 && (
                        <p className="text-xs text-yellow-400">
                          Available balance is 0. Please deposit or switch
                          exchanges.
                        </p>
                      )}
                    </div>

                    <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-[#a3a3a3]">
                          Leverage
                        </p>
                        <span className="text-xs text-text-secondary">
                          Max: {isLoadingSymbolInfo ? "..." : `${maxLeverage}x`}
                        </span>
                      </div>
                      <NumberDraftInput
                        id="trade-leverage-input"
                        min={1}
                        max={maxLeverage}
                        step="1"
                        className="w-full rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-2.5 text-sm text-white focus:border-[#2dcc89] focus:outline-none"
                        value={leverage}
                        onValueChange={(value) =>
                          setLeverage(
                            Math.max(
                              1,
                              Math.min(Math.round(value), maxLeverage),
                            ),
                          )
                        }
                      />
                      <input
                        type="range"
                        min={1}
                        max={maxLeverage}
                        step={1}
                        value={Math.min(leverage, maxLeverage)}
                        onChange={(event) =>
                          setLeverage(Number(event.target.value))
                        }
                        className="w-full accent-emerald-400"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-2">
                        <p className="text-xs font-medium text-[#a3a3a3]">
                          Take Profit{trailingTakeProfit ? "" : " %"}
                        </p>
                        <TakeProfitModeSwitch
                          trailing={trailingTakeProfit}
                          onChange={setTrailingTakeProfit}
                        />
                        {trailingTakeProfit ? (
                          <TrailingTakeProfitInputs
                            activation={trailingActivation}
                            distance={trailingDistance}
                            onActivationChange={setTrailingActivation}
                            onDistanceChange={setTrailingDistance}
                            entryPrice={referencePrice}
                            positionType={positionType}
                          />
                        ) : (
                          <>
                            <PriceMoveRoiHint
                              variant="tp"
                              percentStr={takeProfitPercent}
                              leverage={leverage}
                              marginUsdt={amount}
                            />
                            <input
                              type="number"
                              min={0}
                              max={1000}
                              step="0.1"
                              className="w-full rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-2.5 text-sm text-white focus:border-[#2dcc89] focus:outline-none"
                              value={takeProfitPercent}
                              onChange={(event) =>
                                setTakeProfitPercent(event.target.value)
                              }
                              placeholder="Optional"
                            />
                            <TpSlTargetPrice
                              variant="tp"
                              percentStr={takeProfitPercent}
                              entryPrice={referencePrice}
                              leverage={leverage}
                              positionType={positionType}
                            />
                          </>
                        )}
                      </div>
                      <div className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-4 space-y-2">
                        <p className="text-xs font-medium text-[#a3a3a3]">
                          Stop Loss %
                        </p>
                        <PriceMoveRoiHint
                          variant="sl"
                          percentStr={stopLossPercent}
                          leverage={leverage}
                          marginUsdt={amount}
                        />
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          className="w-full rounded-xl border border-white/10 bg-[#0a0a0a]/60 p-2.5 text-sm text-white focus:border-[#2dcc89] focus:outline-none"
                          value={stopLossPercent}
                          onChange={(event) =>
                            setStopLossPercent(event.target.value)
                          }
                          placeholder="Optional"
                        />
                        <TpSlTargetPrice
                          variant="sl"
                          percentStr={stopLossPercent}
                          entryPrice={referencePrice}
                          leverage={leverage}
                          positionType={positionType}
                        />
                      </div>
                    </div>
                    {trailingTakeProfit && (
                      <TrailingTakeProfitExplainer
                        activation={trailingActivation}
                        distance={trailingDistance}
                        positionType={positionType}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {hasTradableExchange && selectedExchangeCode && (
            <div className="sticky bottom-0 space-y-2 border-t border-white/10 bg-primary/90 p-4 backdrop-blur-md">
              {tradeError && (
                <div className="text-xs text-red-400 text-center">
                  {tradeError}
                </div>
              )}

              {!tradeSuccess ? (
                <button
                  type="button"
                  disabled={isActionDisabled || tradeMutation.isPending}
                  onClick={() => tradeMutation.mutate()}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-white/10 py-3 font-semibold text-white bg-[#07a66c] hover:bg-[#08b877] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {tradeMutation.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {tradeMutation.isPending
                    ? "Submitting trade..."
                    : "Open Trade"}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="text-center text-green-400 font-semibold text-sm">
                    Trade completed
                  </div>
                  <button
                    type="button"
                    onClick={handleGoToPosition}
                    disabled={isFetchingPosition}
                    className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-white/10 py-3 font-semibold text-white bg-[#07a66c] hover:bg-[#08b877] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isFetchingPosition && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Go to position
                  </button>
                </div>
              )}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
